// src/components/telephony/ActiveCallShell.tsx
//
// Active call UI for browser VoIP outbound calls. Renders ONLY when
// activeCall is non-idle. Every visible control is wired to a REAL Twilio
// Voice SDK action via the shared `useTwilioVoiceDevice` instance — no fake
// buttons:
//
//   - Mute / Unmute  → Call.mute(true|false)
//   - End Call       → Call.disconnect()
//   - Keypad (DTMF)  → Call.sendDigits(d)          (Phase 2B)
//   - Mic / Speaker  → AudioHelper device selection (Phase 3)
//   - Dismiss        → clearActiveCall()            (only after ended/failed)
//
// Deliberately NOT exposed (not supported by the CRM backend / this screen):
// Hold, Transfer, Conference, Whisper, Barge.
//
// The DTMF + audio props are optional so existing consumers that don't wire
// them (e.g. PhonePage) keep compiling; the keypad/selectors simply don't
// render there.

import { useEffect, useState } from 'react';
import type { ActiveCallState, AudioDevicesState } from '../../hooks/useTwilioVoiceDevice';
import { InCallRecordingControl } from '../../telephony/activeCallRecording/InCallRecordingControl';
import { ActiveCallRecordingCard } from '../../telephony/activeCallRecording/ActiveCallRecordingCard';
import { GlobalRecordingIndicator } from '../../telephony/activeCallRecording/GlobalRecordingIndicator';

interface ActiveCallShellProps {
  activeCall: ActiveCallState;
  /**
   * Workspace recording policy. When 'always', InCallRecordingControl
   * auto-starts recording once per active call and locks the toggle ON.
   * When 'off' (or undefined) the toggle starts OFF and the user drives
   * every start/stop. This replaces the old static "Recording" badge:
   * the control itself IS the badge, and it's interactive.
   */
  workspaceRecordingMode?: 'off' | 'always';
  onMute: () => void;
  onUnmute: () => void;
  onEndCall: () => void;
  onDismiss: () => void;
  // ── Phase 2B / 3 — optional (undefined on surfaces that don't wire them) ──
  /** Send a single DTMF digit on the active call. Returns true if sent. */
  onSendDigit?: (digit: string) => boolean;
  /** Projected audio-device state from the shared Device (never the SDK). */
  audioDevices?: AudioDevicesState;
  onSelectInput?: (deviceId: string) => Promise<boolean> | boolean;
  onSelectOutput?: (deviceId: string) => Promise<boolean> | boolean;
  /**
   * Optional — inbound "View Details" action. When provided AND the call is
   * active, a "Details" control renders that opens the caller's existing Contact
   * Workspace. Outbound surfaces (PhonePage) never pass it, so the button never
   * appears there and their behaviour is unchanged.
   */
  onViewDetails?: () => void;
}

const DTMF_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'] as const;
const DTMF_TESTID: Record<string, string> = { '*': 'star', '#': 'hash' };

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function CallStatusBadge({ kind }: { kind: ActiveCallState['kind'] }) {
  const config: Record<ActiveCallState['kind'], { label: string; cls: string }> = {
    idle: { label: '', cls: '' },
    connecting: { label: 'Connecting…', cls: 'bg-blue-100 text-blue-800' },
    in_call: { label: 'Active', cls: 'bg-green-100 text-green-800' },
    ended: { label: 'Ended', cls: 'bg-gray-100 text-gray-700' },
    failed: { label: 'Failed', cls: 'bg-red-100 text-red-800' },
  };
  const c = config[kind];
  if (!c.label) return null;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${c.cls}`}>
      {c.label}
    </span>
  );
}

export default function ActiveCallShell({
  activeCall,
  workspaceRecordingMode,
  onMute,
  onUnmute,
  onEndCall,
  onDismiss,
  onSendDigit,
  audioDevices,
  onSelectInput,
  onSelectOutput,
  onViewDetails,
}: ActiveCallShellProps) {
  const [showKeypad, setShowKeypad] = useState(false);

  const isActive = activeCall.kind === 'in_call';

  // Collapse the keypad whenever we leave the active state (parity with
  // Outbound, which hides it by default and per-call).
  useEffect(() => {
    if (!isActive) setShowKeypad(false);
  }, [isActive]);

  if (activeCall.kind === 'idle') return null;

  const displayName =
    'contactName' in activeCall && activeCall.contactName
      ? activeCall.contactName
      : activeCall.toNumber;
  const subline =
    'contactName' in activeCall && activeCall.contactName ? activeCall.toNumber : null;

  const isMuted = isActive && activeCall.muted;
  const isTerminal = activeCall.kind === 'ended' || activeCall.kind === 'failed';

  // Derive props for the mid-call recording control. Visibility is
  // decided inside useActiveCallRecording (based on activeCallKind) —
  // the control renders nothing on idle/ended/failed.
  const recordingCallSid =
    (activeCall.kind === 'in_call' || activeCall.kind === 'connecting')
      ? ((activeCall as { twilioCallSid?: string | null }).twilioCallSid ?? null)
      : null;
  const showRecordingControl = workspaceRecordingMode !== undefined;

  // Audio selectors — mic when there's a choice; speaker only when the SDK
  // reports output selection is supported (Chrome/Edge) AND there's a choice.
  const showMicSelect =
    isActive && !!onSelectInput && !!audioDevices && audioDevices.inputs.length > 1;
  const showSpeakerSelect =
    isActive &&
    !!onSelectOutput &&
    !!audioDevices &&
    audioDevices.outputSelectionSupported &&
    audioDevices.outputs.length > 1;
  const showAudioArea = showMicSelect || showSpeakerSelect;

  return (
    <div
      data-testid="active-call-shell"
      className="fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-1.5rem)] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
      role="dialog"
      aria-label={`Call with ${displayName}`}
    >
      {/* Header */}
      <div className="px-5 py-4 bg-gradient-to-br from-indigo-600 to-indigo-700 text-white">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <div className="text-base font-semibold truncate">{displayName}</div>
            {subline && <div className="text-xs text-indigo-100 truncate">{subline}</div>}
          </div>
          <div className="flex flex-col items-end gap-1">
            <CallStatusBadge kind={activeCall.kind} />
          </div>
        </div>

        {isActive && (
          <div className="mt-3 flex items-center gap-3">
            <div
              data-testid="call-duration"
              className="text-2xl font-mono tabular-nums"
            >
              {formatDuration(activeCall.durationSeconds)}
            </div>
            {/*
              Persistent recording indicator — appears the moment the
              backend confirms recording is active, disappears when it
              stops. Status-only; no actions live here.
            */}
            <GlobalRecordingIndicator
              callSid={recordingCallSid}
              activeCallKind={activeCall.kind}
            />
          </div>
        )}
        {activeCall.kind === 'connecting' && (
          <div className="mt-3 text-sm text-indigo-100">Waiting for the line to connect…</div>
        )}
        {activeCall.kind === 'ended' && (
          <div className="mt-3 text-sm text-indigo-100">
            Duration: {formatDuration(activeCall.durationSeconds)}
          </div>
        )}
        {activeCall.kind === 'failed' && (
          <div data-testid="call-failed-message" className="mt-3 text-sm text-red-100">
            {activeCall.message}
          </div>
        )}
      </div>

      {/* Controls — only real wired actions */}
      <div className="px-5 py-4 bg-gray-50 flex flex-wrap items-center justify-center gap-4">
        {isActive && (
          <>
            <button
              type="button"
              data-testid="call-mute-button"
              onClick={() => (isMuted ? onUnmute() : onMute())}
              className={`flex flex-col items-center justify-center w-16 h-16 rounded-full font-medium text-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                isMuted
                  ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
            >
              <span className="text-base">{isMuted ? '🔇' : '🎤'}</span>
              <span className="mt-0.5">{isMuted ? 'Muted' : 'Mute'}</span>
            </button>

            {onSendDigit && (
              <button
                type="button"
                data-testid="call-keypad-toggle"
                aria-pressed={showKeypad}
                onClick={() => setShowKeypad((v) => !v)}
                className={`flex flex-col items-center justify-center w-16 h-16 rounded-full font-medium text-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                  showKeypad
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                aria-label={showKeypad ? 'Hide keypad' : 'Show keypad'}
              >
                <span className="text-base">⌨️</span>
                <span className="mt-0.5">Keypad</span>
              </button>
            )}

            {onViewDetails && (
              <button
                type="button"
                data-testid="call-view-details-button"
                onClick={onViewDetails}
                className="flex flex-col items-center justify-center w-16 h-16 rounded-full bg-gray-200 text-gray-700 font-medium text-xs hover:bg-gray-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                aria-label="View Details"
              >
                <span className="text-base">👤</span>
                <span className="mt-0.5">Details</span>
              </button>
            )}

            <button
              type="button"
              data-testid="call-end-button"
              onClick={onEndCall}
              className="flex flex-col items-center justify-center w-16 h-16 rounded-full bg-red-600 text-white font-medium text-xs hover:bg-red-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
              aria-label="End call"
            >
              <span className="text-base">📞</span>
              <span className="mt-0.5">End</span>
            </button>
          </>
        )}

        {activeCall.kind === 'connecting' && (
          <button
            type="button"
            data-testid="call-cancel-button"
            onClick={onEndCall}
            className="flex flex-col items-center justify-center w-16 h-16 rounded-full bg-red-600 text-white font-medium text-xs hover:bg-red-700 transition-colors"
            aria-label="Cancel call"
          >
            <span className="text-base">📞</span>
            <span className="mt-0.5">Cancel</span>
          </button>
        )}

        {isTerminal && (
          <button
            type="button"
            data-testid="call-dismiss-button"
            onClick={onDismiss}
            className="px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            Dismiss
          </button>
        )}
      </div>

      {/* Mid-call recording control. Visibility is decided inside the
          hook based on activeCallKind — during connecting/in_call it
          renders the toggle; on ended/failed/idle it renders null.
          workspaceRecordingMode drives compliance lock + auto-start. */}
      {showRecordingControl && (
        <div className="px-5 pb-3 bg-gray-50">
          {/*
            New Recording Mode UX — the integrated card is the only
            in-call recording surface. It handles idle / recording /
            stopped / compliance-locked states with the same visual
            language everywhere. Replaces the standalone toggle
            (`InCallRecordingControl`) which remains exported for
            other consumers during transition but is no longer
            rendered here.
          */}
          <ActiveCallRecordingCard
            callSid={recordingCallSid}
            workspaceRecordingMode={workspaceRecordingMode!}
            activeCallKind={activeCall.kind}
          />
        </div>
      )}
      {/* Suppress unused-import warning during transition. */}
      {false && <InCallRecordingControl callSid={null} workspaceRecordingMode={undefined} activeCallKind="" />}

      {/* DTMF keypad — collapsible, real Call.sendDigits (Phase 2B) */}
      {isActive && onSendDigit && showKeypad && (
        <div data-testid="call-keypad" className="px-5 pb-4 bg-gray-50">
          <p className="text-[11px] text-gray-500 mb-2 text-center">Press digits during the call</p>
          <div className="grid grid-cols-3 gap-2 max-w-[220px] mx-auto">
            {DTMF_KEYS.map((d) => (
              <button
                key={d}
                type="button"
                data-testid={`call-dtmf-${DTMF_TESTID[d] ?? d}`}
                onClick={() => onSendDigit(d)}
                disabled={!isActive}
                aria-label={`Send DTMF ${d}`}
                className="py-2.5 rounded-lg bg-white border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Audio device selectors — mic always when there's a choice; speaker
          only where the SDK reports output selection is supported (Phase 3) */}
      {showAudioArea && (
        <div data-testid="call-audio-devices" className="px-5 pb-4 bg-gray-50 space-y-2">
          {showMicSelect && (
            <label className="flex items-center gap-2">
              <span className="text-[11px] text-gray-500 w-16">Mic:</span>
              <select
                data-testid="call-mic-select"
                value={audioDevices!.selectedInputId ?? ''}
                onChange={(e) => void onSelectInput!(e.target.value)}
                className="flex-1 text-xs px-2 py-1.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                aria-label="Select microphone"
              >
                {audioDevices!.inputs.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          {showSpeakerSelect && (
            <label className="flex items-center gap-2">
              <span className="text-[11px] text-gray-500 w-16">Speaker:</span>
              <select
                data-testid="call-speaker-select"
                value={audioDevices!.selectedOutputId ?? ''}
                onChange={(e) => void onSelectOutput!(e.target.value)}
                className="flex-1 text-xs px-2 py-1.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                aria-label="Select speaker"
              >
                {audioDevices!.outputs.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}
    </div>
  );
}
